import React from 'react';
import '../styles/legal.css';

const Terms = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1 className="legal-title">ADGRO TERMS OF SERVICE</h1>
        <p className="legal-subtitle">(Master SaaS Agreement)</p>

        <div className="legal-meta">
          <p><strong>Effective Date:</strong> January 18, 2026</p>
          <p><strong>Last Updated:</strong> January 18, 2026</p>
        </div>

        <p className="legal-intro">
          These Terms of Service ("Terms") constitute a legally binding agreement between Rhombus Labs Inc., a Canadian corporation ("Rhombus Labs", "we", "us", or "our"), and the retailer, grocery store, or business accepting these Terms ("Customer", "you", or "your"). By accessing or using the Adgro platform (the "Service"), you acknowledge that you have read, understood, and agree to be bound by these Terms. Adgro is a product developed and operated by Rhombus Labs Inc.
        </p>
        <p>
          If you are accepting these Terms on behalf of a company or other legal entity, you represent that you have the authority to bind such entity to these Terms.
        </p>

        <section className="legal-section">
          <h2>1. DESCRIPTION OF SERVICE</h2>
          <p>Adgro provides a cloud-based advertising platform that enables retailers and grocery stores to manage and optimize their digital advertising campaigns. The Service includes:</p>
          <ul>
            <li>Digital advertising campaign creation and management</li>
            <li>Audience targeting and segmentation tools</li>
            <li>Ad performance analytics and reporting</li>
            <li>Behavioral tracking and attribution</li>
            <li>AI-assisted campaign optimization</li>
            <li>Integration with retail systems and platforms</li>
          </ul>
          <p><strong>IMPORTANT:</strong> Adgro is a technology service provider. Adgro does not independently advertise to consumers on its own behalf. Customer is responsible for the content, targeting, and legal compliance of all advertising campaigns created using the Service.</p>
        </section>

        <section className="legal-section">
          <h2>2. LICENSE GRANT</h2>
          <p>Subject to Customer's compliance with these Terms and payment of applicable fees, Adgro grants Customer a limited, non-exclusive, non-transferable, non-sublicensable right to access and use the Service solely for Customer's internal business purposes during the subscription term.</p>
          <p>Customer may not:</p>
          <ul>
            <li>Copy, modify, or create derivative works of the Service</li>
            <li>Reverse engineer, disassemble, or decompile the Service</li>
            <li>Resell, sublicense, or transfer access to the Service</li>
            <li>Use the Service for any unlawful purpose</li>
            <li>Interfere with or disrupt the integrity or performance of the Service</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. CUSTOMER RESPONSIBILITIES</h2>
          <p>Customer acknowledges and agrees that:</p>

          <h3>3.1 Data Custodianship</h3>
          <ul>
            <li>Customer is the custodian, controller, and owner of all business data and customer data ("Customer Data") processed through the Service.</li>
            <li>Customer retains all right, title, and interest in and to Customer Data.</li>
          </ul>

          <h3>3.2 Consent and Compliance Responsibility</h3>
          <ul>
            <li>Customer is solely responsible for obtaining and maintaining all consents, authorizations, and notices required by applicable law for any advertising, tracking, or data collection conducted through the Service.</li>
            <li>Customer determines the content, targeting, and recipients of all advertising campaigns.</li>
            <li>Customer must ensure all necessary consents are obtained before using tracking and targeting features.</li>
            <li>Adgro facilitates advertising services on Customer's behalf but does not independently obtain consumer consent.</li>
          </ul>

          <h3>3.3 Advertising Content</h3>
          <ul>
            <li>Customer is solely responsible for the accuracy, quality, legality, and compliance of all advertising content.</li>
            <li>Customer must ensure all advertising complies with applicable laws, including advertising standards, consumer protection laws, and competition laws.</li>
            <li>Customer is responsible for maintaining appropriate security of account credentials.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. DATA OWNERSHIP AND PROCESSING</h2>

          <h3>4.1 Ownership</h3>
          <p>Customer retains all right, title, and interest in and to Customer Data. Nothing in these Terms transfers ownership of Customer Data to Adgro.</p>

          <h3>4.2 Adgro's Role</h3>
          <p>Adgro acts solely as a service provider and data processor. Adgro processes Customer Data only:</p>
          <ul>
            <li>As necessary to provide the Service</li>
            <li>In accordance with Customer's documented instructions</li>
            <li>As required by applicable law</li>
          </ul>

          <h3>4.3 Aggregated Data</h3>
          <p>Adgro may use aggregated, anonymized data derived from Customer's use of the Service for purposes of improving the Service, benchmarking, and industry analysis, provided such data does not identify Customer or any individual.</p>

          <h3>4.4 Data Processing Addendum</h3>
          <p>The processing of personal information is further governed by the <a href="/dpa">Data Processing Addendum</a>, which is incorporated by reference into these Terms.</p>
        </section>

        <section className="legal-section">
          <h2>5. AI FEATURES AND AUTOMATED DECISION-MAKING</h2>
          <p>The Service includes AI-assisted features for campaign optimization, audience targeting, and performance analysis. Customer acknowledges and agrees that:</p>

          <h3>5.1 AI Limitations</h3>
          <ul>
            <li>AI outputs are informational and intended to assist advertising decisions.</li>
            <li>AI outputs may contain errors or inaccuracies and should be reviewed by qualified personnel.</li>
            <li>AI does not replace human judgment or professional decision-making.</li>
          </ul>

          <h3>5.2 Algorithm Transparency</h3>
          <ul>
            <li>Adgro uses machine learning to optimize ad delivery, audience targeting, and campaign performance.</li>
            <li>Algorithms analyze behavioral data, campaign metrics, and market trends.</li>
            <li>Customer retains control over campaign parameters and can override automated recommendations.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>6. COMPLIANCE WITH LAW</h2>
          <p>Customer is responsible for ensuring its use of the Service complies with all applicable laws and regulations, including but not limited to:</p>
          <ul>
            <li>Personal Information Protection and Electronic Documents Act (PIPEDA)</li>
            <li>Applicable provincial privacy legislation</li>
            <li>Canada's Anti-Spam Legislation (CASL)</li>
            <li>Competition Act and advertising standards</li>
            <li>Any other applicable federal, provincial, or local privacy, advertising, or consumer protection regulations</li>
          </ul>
          <p>Adgro will provide reasonable cooperation to assist Customer in meeting its compliance obligations, subject to the terms of the Data Processing Addendum.</p>
        </section>

        <section className="legal-section">
          <h2>7. FEES AND PAYMENT</h2>

          <h3>7.1 Subscription Fees</h3>
          <p>Customer agrees to pay all fees specified in the applicable order form or pricing plan. Fees are quoted in Canadian dollars unless otherwise specified.</p>

          <h3>7.2 Payment Terms</h3>
          <ul>
            <li>Monthly subscriptions are billed in advance on the first day of each billing period.</li>
            <li>Annual subscriptions are billed in advance for the full year.</li>
            <li>All fees are non-refundable except as expressly stated in these Terms.</li>
          </ul>

          <h3>7.3 Taxes</h3>
          <p>Fees are exclusive of applicable taxes (including HST/GST). Customer is responsible for all applicable taxes.</p>

          <h3>7.4 Late Payment</h3>
          <p>Adgro may suspend access to the Service for accounts with payments overdue by more than 30 days.</p>
        </section>

        <section className="legal-section">
          <h2>8. TERM AND TERMINATION</h2>

          <h3>8.1 Term</h3>
          <p>These Terms commence upon Customer's acceptance and continue for the subscription term specified in the applicable order form, unless earlier terminated.</p>

          <h3>8.2 Termination for Convenience</h3>
          <ul>
            <li>Monthly subscriptions may be cancelled at any time with effect at the end of the current billing period.</li>
            <li>Annual subscriptions may be cancelled with 30 days' notice before renewal.</li>
          </ul>

          <h3>8.3 Termination for Cause</h3>
          <p>Either party may terminate immediately upon written notice if the other party materially breaches these Terms and fails to cure such breach within 30 days of receiving notice.</p>

          <h3>8.4 Effect of Termination</h3>
          <p>Upon termination: (a) Customer's access to the Service will cease; (b) Customer remains responsible for any outstanding fees; (c) Adgro will delete Customer Data in accordance with the Data Processing Addendum.</p>
        </section>

        <section className="legal-section">
          <h2>9. CONFIDENTIALITY</h2>
          <p>Each party agrees to maintain the confidentiality of the other party's confidential information and not to use or disclose such information except as necessary to perform its obligations under these Terms or as required by law.</p>
        </section>

        <section className="legal-section">
          <h2>10. INTELLECTUAL PROPERTY</h2>
          <p>Adgro retains all right, title, and interest in and to the Service, including all intellectual property rights. These Terms do not grant Customer any rights to Adgro's trademarks, service marks, or logos.</p>
        </section>

        <section className="legal-section">
          <h2>11. WARRANTIES AND DISCLAIMERS</h2>

          <h3>11.1 Limited Warranty</h3>
          <p>Adgro warrants that the Service will perform substantially in accordance with the applicable documentation during the subscription term.</p>

          <h3>11.2 Disclaimer</h3>
          <p className="legal-disclaimer">
            EXCEPT AS EXPRESSLY SET FORTH HEREIN, THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. ADGRO DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE. ADGRO DOES NOT GUARANTEE ANY SPECIFIC ADVERTISING RESULTS OR PERFORMANCE OUTCOMES.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. LIMITATION OF LIABILITY</h2>
          <p className="legal-disclaimer">
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:
          </p>
          <ul>
            <li>ADGRO'S TOTAL AGGREGATE LIABILITY UNDER THESE TERMS SHALL NOT EXCEED THE TOTAL FEES PAID BY CUSTOMER TO ADGRO IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.</li>
            <li>IN NO EVENT SHALL ADGRO BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.</li>
          </ul>
          <p>These limitations apply regardless of the theory of liability (contract, tort, or otherwise) and shall survive termination of these Terms.</p>
        </section>

        <section className="legal-section">
          <h2>13. INDEMNIFICATION</h2>
          <p>Customer agrees to indemnify, defend, and hold harmless Adgro and its officers, directors, employees, and agents from and against any claims, damages, losses, or expenses (including reasonable legal fees) arising out of or related to:</p>
          <ul>
            <li>Customer's use of the Service</li>
            <li>Customer Data and advertising content</li>
            <li>Customer's breach of these Terms</li>
            <li>Customer's violation of applicable law or third-party rights</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>14. GOVERNING LAW AND DISPUTE RESOLUTION</h2>
          <p>These Terms are governed by and construed in accordance with the laws of the Province of Ontario and the federal laws of Canada applicable therein, without regard to conflict of law principles.</p>
          <p>Any dispute arising out of or relating to these Terms shall be resolved exclusively in the courts located in Toronto, Ontario, and each party consents to the personal jurisdiction of such courts.</p>
        </section>

        <section className="legal-section">
          <h2>15. GENERAL PROVISIONS</h2>

          <h3>15.1 Entire Agreement</h3>
          <p>These Terms, together with the <a href="/dpa">Data Processing Addendum</a>, <a href="/privacy">Privacy Policy</a>, <a href="/cookies">Cookie Policy</a>, and <a href="/acceptable-use">Acceptable Use Policy</a> (each incorporated by reference), constitute the entire agreement between the parties.</p>

          <h3>15.2 Amendments</h3>
          <p>Adgro may update these Terms from time to time. Material changes will be communicated to Customer at least 30 days before taking effect.</p>

          <h3>15.3 Severability</h3>
          <p>If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.</p>

          <h3>15.4 Waiver</h3>
          <p>No failure or delay by either party in exercising any right shall constitute a waiver of that right.</p>

          <h3>15.5 Assignment</h3>
          <p>Customer may not assign these Terms without Adgro's prior written consent. Adgro may assign these Terms in connection with a merger, acquisition, or sale of assets.</p>

          <h3>15.6 Force Majeure</h3>
          <p>Neither party shall be liable for any delay or failure to perform due to causes beyond its reasonable control.</p>
        </section>

        <section className="legal-section">
          <h2>16. CONTACT INFORMATION</h2>
          <p>For questions about these Terms, please contact:</p>
          <div className="legal-contact">
            <p><strong>Rhombus Labs Inc.</strong></p>
            <p>Email: <a href="mailto:support@adgro.ca">support@adgro.ca</a></p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Terms;
